import { FlaskConical, HardDriveDownload, Server, Wrench } from 'lucide-react';
import { AutoFixArt, RestoreTestArt } from '@/modules/marketing/AutomationArt';
import { BackupArt, ServerArt } from '@/modules/marketing/FeatureArt';
import type { Article } from '@/modules/marketing/article-types';

/**
 * The articles for what keeps running after automation: auto-fix, backups, backup verification, and
 * the servers everything else works on.
 *
 * Each claim is one the Monitoring, Backups or Servers module makes in code — the four fix actions
 * and the four things never done, the retention rule, the weekly restore test, the privilege modes —
 * so nothing here promises more than the platform does.
 */

export const autoFix: Article = {
  slug: 'auto-fix',
  category: 'features',
  icon: Wrench,
  art: AutoFixArt,
  eyebrow: 'Monitoring',
  title: 'Auto-Fix',
  seoTitle: 'Auto-Fix: Automated Server Remediation',
  metaDescription:
    'When a monitored server fails a check, Auto-Fix tries a safe repair first: restart a stopped service, reload PHP-FPM or free disk space, then alerts if needed.',
  standfirst:
    'Many outages have simple causes: a service stopped and did not restart, a PHP-FPM pool hung, or package caches filled the disk. When a monitor attached to a server fails, the platform tries a safe fix first and records what it did.',
  sections: [
    {
      heading: 'Four safe fixes',
      paragraphs: [
        '**Restart stopped services.** Web server, PHP, database and application services that are stopped or failed are started. Services that are already running are not touched.',
        '**Reload PHP-FPM.** A graceful reload that lets requests in progress finish.',
        '**Free disk space.** Clears package caches, trims the system journal to two weeks and removes the platform’s own temporary files.',
        '**Reload the web server.** After testing its configuration, so a certificate already renewed on disk is actually served.',
      ],
    },
    {
      heading: 'Fixes matched to the failure',
      paragraphs: [
        'The fix depends on what the check saw. A site that does not respond may have a stopped service. A 502, 503 or 504 from a gateway may point to a failed PHP pool. A failing disk check gets the disk fix, and a failing SSL check gets a web server reload.',
        'A 500 or 404 gets no fix. Those responses come from an application that is running, and restarting services underneath it would not address the cause. When no fix applies, the alert is sent straight away.',
      ],
    },
    {
      heading: 'What Auto-Fix never does',
      paragraphs: [
        'It never reboots a server, deletes your data, changes firewall rules or rolls back a deployment.',
        'Each of these can turn a short outage into a longer one, and each is a decision that should be made by a person.',
      ],
    },
    {
      heading: 'Alerts are delayed, not suppressed',
      paragraphs: [
        'While a fix runs, which takes about a minute, the down alert waits. If the check still fails afterwards, the alert is sent with the fix that was attempted. If the fix works, the incident records what was done, and you hear about it afterwards in a summary sent where your alerts go, at most once an hour.',
        'Auto-Fix runs at most three times per monitor per day. After that, the platform stops trying and sends the alert, because a failure that keeps coming back needs a person to investigate.',
      ],
    },
  ],
  faq: [
    {
      q: 'Which monitors can use Auto-Fix?',
      a: 'Monitors attached to one of your servers, because a fix needs a server to run on. Auto-Fix is on by default for those monitors and can be turned off for any of them.',
    },
    {
      q: 'Will Auto-Fix reboot my server?',
      a: 'No. The platform never reboots a server on its own, and it never deletes your data, changes firewall rules or rolls back a deployment.',
    },
    {
      q: 'Will I still be alerted?',
      a: 'Yes, whenever a fix does not resolve the problem. The alert waits about a minute while the fix runs, then includes what was attempted. Successful fixes are recorded on the incident.',
    },
    {
      q: 'What stops it restarting a crashing service in a loop?',
      a: 'A limit of three automatic fixes per monitor per day. After that, the platform stops trying and sends the alert.',
    },
    {
      q: 'What does the disk space fix delete?',
      a: 'Package manager caches, system journal entries older than two weeks, and temporary files the platform created. Your files, logs and databases are not touched.',
    },
  ],
  points: [
    'Restarts services, reloads PHP-FPM, frees disk space, reloads the web server',
    'Fix chosen from the failure the check detected',
    'Never reboots, deletes data, changes firewall rules or rolls back',
    'Alert sent if the fix does not work',
    'At most three fixes per monitor per day',
  ],
  related: [
    { label: 'Website monitoring', to: '/monitoring/website-monitoring' },
    { label: 'Linux server playbooks', to: '/features/playbooks' },
  ],
};

export const backups: Article = {
  slug: 'backups',
  category: 'features',
  icon: HardDriveDownload,
  art: BackupArt,
  eyebrow: 'Backups',
  title: 'Server Backups',
  seoTitle: 'Linux Server Backups to S3-Compatible Storage',
  metaDescription:
    'Schedule backups of directories and MariaDB, MySQL or PostgreSQL databases to S3-compatible storage you own, with retention that keeps your last good copy.',
  standfirst:
    'Back up directories and MariaDB, MySQL or PostgreSQL databases on your servers to storage you own. Backups run on a schedule and upload directly from your server to your bucket. The platform does not keep a copy.',
  sections: [
    {
      heading: 'Backup sources',
      paragraphs: [
        'A backup source is a directory, a database or both on one of your servers. Databases are read with the server’s own administrator access: root for MariaDB and MySQL, and the postgres account for PostgreSQL. You do not need to provide a database password.',
        'Top-level system directories such as /etc and /usr cannot be used as sources, because restoring one would replace part of an operating system while it runs.',
      ],
    },
    {
      heading: 'Direct uploads to your storage',
      paragraphs: [
        'Back up to Amazon S3, Backblaze B2, DigitalOcean Spaces, Hetzner Object Storage, Wasabi, Cloudflare R2 or any other S3-compatible storage. Your storage secret is encrypted on the platform and used only to sign short-lived upload links. It is never sent to your server, and archives go straight from your server to your bucket.',
        'Large backups are uploaded in 100 MB pieces while they are being created, so the server never needs free space for the whole archive.',
        'You can also keep backups on the server itself. These are labelled as server-only copies, because they are lost if the server is.',
      ],
    },
    {
      heading: 'Schedules and retention',
      paragraphs: [
        'Run backups daily or weekly, as a full backup or database only, and keep between 1 and 30 copies per destination. Retention is counted per destination, so a nightly local copy and a weekly offsite copy each keep their own history.',
        'Retention counts only successful backups. A series of failed backups can never push your last good copy out.',
      ],
    },
    {
      heading: 'Failure alerts',
      paragraphs: [
        'A failed scheduled backup emails the account with the reason. A schedule that is switched on but has stopped producing backups is flagged as not running.',
        'Before building an archive, the platform checks the server has enough free space for the files, the database dump and some headroom, so a backup does not fill the disk it is protecting.',
      ],
    },
    {
      heading: 'Restores, including to another server',
      paragraphs: [
        'Before a restore, the archive is checked against its recorded SHA-256 checksum. Existing files are moved aside rather than deleted, and you confirm by typing the source name. A backup in cloud storage can be restored to a different server, which moves the source there.',
        'Backups are standard .tar.gz archives with a standard SQL dump inside. You can download them from your bucket and restore them with standard tools, without the platform.',
      ],
    },
  ],
  faq: [
    {
      q: 'What can I back up?',
      a: 'Any directory on your server except top-level system directories, a MariaDB, MySQL or PostgreSQL database, or both. Sources with both can also be backed up database only.',
    },
    {
      q: 'Do you store my backups?',
      a: 'No. Archives upload directly from your server to your bucket, or stay on your server if you choose that destination. Downloads of a server-side copy are streamed through the platform and not stored.',
    },
    {
      q: 'Is my storage key sent to my server?',
      a: 'No. The secret is encrypted on the platform and used only to sign short-lived links for uploading, downloading and removing old backups.',
    },
    {
      q: 'Can retention delete my only backup?',
      a: 'No. Retention counts only successful backups, so failed runs never push out your last good copy.',
    },
    {
      q: 'How do I know a backup will restore?',
      a: 'Every backup is checked at its destination as soon as it is written, and each source’s newest backup is restore-tested every week. See Backup Verification.',
    },
  ],
  points: [
    'Directories and MariaDB, MySQL or PostgreSQL databases',
    'S3-compatible storage you own, or the server itself',
    'Daily or weekly, keeping 1 to 30 copies',
    'Retention never removes the last good copy',
    'Checksum verified before restore; restore to another server',
  ],
  related: [
    { label: 'Backup verification', to: '/features/backup-verification' },
    { label: 'Server management', to: '/features/server-management' },
  ],
};

export const backupVerification: Article = {
  slug: 'backup-verification',
  category: 'features',
  icon: FlaskConical,
  art: RestoreTestArt,
  eyebrow: 'Backups',
  title: 'Backup Verification',
  seoTitle: 'Backup Verification and Restore Testing',
  metaDescription:
    'Every backup is verified at its destination, and each source’s newest backup is restore-tested weekly in a scratch database that is dropped afterwards.',
  standfirst:
    'Every backup is checked at its destination as soon as it is written. Once a week, each source’s newest backup is restored into a scratch database on its own server, read back table by table, and removed.',
  sections: [
    {
      heading: 'Checked at the destination',
      paragraphs: [
        'As soon as a backup is written, the platform checks that the copy at its destination exists and is the size that was written: a signed request for the object in cloud storage, or a look at the file on the server’s own disk. A copy that passes is marked as stored.',
        'A copy that is definitely missing or the wrong size fails the backup that night. An answer that proves nothing either way, such as storage refusing the request, is recorded as could not run and the backup stands.',
      ],
    },
    {
      heading: 'Weekly restore tests',
      paragraphs: [
        'Each source’s newest good backup is fetched onto its own server and compared with the SHA-256 checksum recorded when it was taken. The archive is then read to confirm it is valid.',
        'The database dump is loaded into a scratch database with a name the platform generates, never the live database, and every table is read back. The scratch database is dropped afterwards, including when the test fails.',
      ],
    },
    {
      heading: 'What reading every table proves',
      paragraphs: [
        'On MySQL and MariaDB each table is checked with CHECK TABLE. On PostgreSQL every table is counted in full, which reads every data page and fails on a damaged one. A dump that loads but holds a table that cannot be read fails the test.',
        'Scheduled events are removed from a MySQL dump before it is loaded, because an event runs the moment it exists and can write to other databases on the same server, including the live one.',
      ],
    },
    {
      heading: 'A copy that fails stops counting',
      paragraphs: [
        'A backup that fails verification is marked as failed and retention no longer counts it. Keeping three copies then means keeping three that work: if the newest copies all fail, the older one that would still restore is not the one deleted.',
      ],
    },
    {
      heading: 'Live data is not touched',
      paragraphs: [
        'Restore tests do not read or write the source directory and never target the live database. The work runs at the lowest CPU and disk priority the server offers, so the server keeps serving traffic while its backup is tested.',
        'The space a test needs is measured before anything is written, and on MySQL the scratch load is kept out of the binary log, so replicas never receive it. Tests run in the hours after the source’s own scheduled backups, when the server already expects heavy work.',
        'You can also test any backup yourself with Test restore.',
      ],
    },
    {
      heading: 'Failed versus could not run',
      paragraphs: [
        'If a test cannot run, for example because the server is unreachable or its disk is full, the result says nothing about the backup. It is shown as a warning, not as a broken backup.',
        'A test that shows a backup would not restore is recorded as failed and emails the account.',
      ],
    },
  ],
  faq: [
    {
      q: 'How often are backups restore-tested?',
      a: 'Each source’s newest good backup is restore-tested every seven days. You can test any backup yourself at any time.',
    },
    {
      q: 'Does a restore test touch my live database?',
      a: 'No. The dump is loaded into a separate scratch database with a generated name, which is dropped afterwards.',
    },
    {
      q: 'Where does the test run?',
      a: 'On the server the backup came from. The platform does not copy your data to its own infrastructure to test it.',
    },
    {
      q: 'What if a test cannot run?',
      a: 'It is recorded as could not run, with the reason, and tried again later. It does not mark the backup as broken.',
    },
    {
      q: 'Am I notified when a backup fails verification?',
      a: 'Yes. A failed verification emails the account and appears on the backups page and the dashboard.',
    },
  ],
  points: [
    'Every backup checked at its destination',
    'Weekly restore test of each source’s newest backup',
    'Checksum compared, archive read, database loaded and read back',
    'Scratch database dropped; live data untouched',
    'Could not run reported separately from failed',
  ],
  related: [
    { label: 'Server backups', to: '/features/backups' },
    { label: 'Auto-Fix', to: '/features/auto-fix' },
  ],
};

export const serverManagement: Article = {
  slug: 'server-management',
  category: 'features',
  icon: Server,
  art: ServerArt,
  eyebrow: 'Servers',
  title: 'Server Management',
  seoTitle: 'Agentless Linux Server Management over SSH',
  metaDescription:
    'Connect Linux servers from any provider over SSH, as root or a sudo user. The platform detects the distribution, package manager, privileges and hardware.',
  standfirst:
    'Connect any Linux server you can reach over SSH, from any provider, as root or a user with sudo. The platform detects the distribution, package manager, root access, CPU, memory and disk, so you do not have to enter them.',
  sections: [
    {
      heading: 'Use the servers you already have',
      paragraphs: [
        'Techie Gain does not sell or host servers. You keep your existing provider and connect your servers to the platform.',
        'If you cancel, your servers keep running exactly as they are.',
      ],
    },
    {
      heading: 'Detected server details',
      paragraphs: [
        'Each connection check records the distribution and version, architecture, installed package manager, service manager, CPU cores, memory, disk size and free space, kernel, and the account used to connect.',
        'Playbooks use these details to choose the right commands, and you can target runs by them, for example every Debian server.',
      ],
    },
    {
      heading: 'Root access is detected',
      paragraphs: [
        'Connect as root, as a user with passwordless sudo, or as a user whose sudo password you store with the credential. The platform detects which applies, and tells you when an account cannot become root and what to change.',
        'Passwords and keys are encrypted before they are stored and are never shown again. Sudo passwords are passed to sudo on standard input, never on the command line where other users on the server could read them.',
      ],
    },
    {
      heading: 'Host key pinning',
      paragraphs: [
        'The server’s SSH host key is recorded on the first connection and verified on every connection after that. If the key changes, the connection is stopped and the reason is shown.',
        'This can interrupt access after a server is rebuilt, and it is what protects you from connecting to the wrong machine.',
      ],
    },
    {
      heading: 'Resource history without an agent',
      paragraphs: [
        'CPU, memory, disk, load and network usage are collected over SSH every five minutes and kept for 30 days. Periods with no readings are shown as gaps, not filled in.',
        'Nothing is installed or left running on the server between checks.',
      ],
    },
  ],
  faq: [
    {
      q: 'Which Linux distributions can I connect?',
      a: 'Any Linux server reachable over SSH can be connected and can run commands. Playbooks and templates support Ubuntu 22.04 and later, Debian 12 and later, and RHEL-compatible 8 and later (AlmaLinux, Rocky Linux, RHEL, CentOS Stream and Oracle Linux), and most run on Amazon Linux 2023. Each one lists the versions it accepts. Other distributions, such as Fedora, openSUSE, Arch or Alpine, show as Unsupported OS: playbooks refuse them, while commands, monitoring and backups still work.',
    },
    {
      q: 'Do I have to connect as root?',
      a: 'No. A user with passwordless sudo works, and so does a user whose sudo asks for a password, if you store that password with the credential.',
    },
    {
      q: 'Do I need to install an agent?',
      a: 'No. Everything runs over the SSH connection you configure, including metrics collection.',
    },
    {
      q: 'Do you sell servers?',
      a: 'No. You bring servers from any provider. Techie Gain is not a reseller and is not part of your relationship with your host.',
    },
    {
      q: 'What happens to my servers if I cancel?',
      a: 'Nothing. They keep running as they are. You lose the automation, monitoring and backups the platform was running, not your servers.',
    },
  ],
  points: [
    'Linux servers from any provider, over SSH',
    'Root, passwordless sudo or sudo with a stored password',
    'Distribution, package manager, CPU, memory and disk detected',
    'SSH host key pinned on first connection',
    'Five-minute metrics kept for 30 days, with no agent',
  ],
  related: [
    { label: 'Linux server playbooks', to: '/features/playbooks' },
    { label: 'Command runner', to: '/features/command-runner' },
  ],
};
